import { useState, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { intl } from '../../../../../../../i18n'
import { useVersionReportTemplateRepository } from '../../../../../../../services/adaptors/versionReportTemplateRepositoryAdaptor'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../../store/messages'
import {
  updateTemplate,
  VersionReportTemplateEntity,
} from '../../../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { CreateVersionReportTemplatePageParams } from '../../../../../../../applications/ports/versionReportTemplateRepository'
import { VersionReportTemplatePageFormModel } from '../model/VersionReportTemplatePageFormModel'

export type UseTemplateDialogProps = {
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  resetPageEntityMap: () => void
  original?: VersionReportTemplateEntity
}

export type UseTemplateDialogReturn = {
  templateName: string
  setTemplateName: (name: string) => void
  loading: boolean
  handleSubmit: (pages: VersionReportTemplatePageFormModel[]) => Promise<void>
  handleClose: () => void
}

export const useTemplateDialog = ({
  onAfterSubmit,
  onClose,
  resetPageEntityMap,
  original,
}: UseTemplateDialogProps): UseTemplateDialogReturn => {
  const [templateName, setTemplateName] = useState('')
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const { create, update } = useVersionReportTemplateRepository()

  useEffect(() => {
    if (original) {
      setTemplateName(original.name)
    } else {
      setTemplateName('')
    }
  }, [original])

  const onAfterSubmitInternal = useCallback(async () => {
    onAfterSubmit()
    setTemplateName('')
    resetPageEntityMap()
    onClose()
  }, [onAfterSubmit, onClose, resetPageEntityMap])

  const onCreate = useCallback(
    async (pages: VersionReportTemplatePageFormModel[]) => {
      try {
        await create(
          templateName.trim(),
          pages.map(({ uuid, ...rest }) => ({
            pageTitle: rest.pageTitle,
            pageNumber: rest.pageNumber,
            pageType: rest.pageType,
            config: rest.config,
          }))
        )
        onAfterSubmitInternal()
        dispatch(
          addGlobalMessage({
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
            text: `テンプレート「${templateName}」が作成されました（${pages.length}ページ）`,
          })
        )
      } catch (error) {
        console.error('Template creation failed:', error)
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: 'テンプレート作成エラー',
            text: 'テンプレート作成に失敗しました',
          })
        )
        throw error
      }
    },
    [create, templateName, onAfterSubmitInternal, dispatch]
  )

  const onUpdate = useCallback(
    async (pages: VersionReportTemplatePageFormModel[]) => {
      if (!original) return

      try {
        const newEntity = updateTemplate(
          original,
          templateName,
          pages.map(page => ({
            uuid: page.uuid!,
            pageTitle: page.pageTitle,
            pageNumber: page.pageNumber,
            pageType: page.pageType,
            config: page.config,
          }))
        )
        await update(newEntity)
        onAfterSubmitInternal()
        dispatch(
          addGlobalMessage({
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'update.completed.title' }),
            text: `テンプレート「${templateName}」が更新されました（${pages.length}ページ）`,
          })
        )
      } catch (error) {
        console.error('Template update failed:', error)
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: 'テンプレート更新エラー',
            text: 'テンプレート更新に失敗しました',
          })
        )
        throw error
      }
    },
    [original, templateName, update, onAfterSubmitInternal, dispatch]
  )

  const handleSubmit = useCallback(
    async (pages: VersionReportTemplatePageFormModel[]) => {
      try {
        setLoading(true)

        if (original) {
          await onUpdate(pages)
        } else {
          await onCreate(pages)
        }
      } catch (error) {
        // エラーメッセージは各関数内で処理済み
      } finally {
        setLoading(false)
      }
    },
    [original, onCreate, onUpdate]
  )

  const handleClose = useCallback(() => {
    if (!loading) {
      setTemplateName('')
      resetPageEntityMap()
      onClose()
    }
  }, [loading, resetPageEntityMap, onClose])

  return {
    templateName,
    setTemplateName,
    loading,
    handleSubmit,
    handleClose,
  }
}
